import type { Moment } from 'moment'
import { rem } from 'polished'
import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import FlightDurationArrow from './FlightDurationArrow'
import FlightItemCardHeading from './FlightItemCardHeading'
import FlightLocationGroup from './FlightLocationGroup'
import FlightTopContent from '../Common/FlightTopContent'
import type { ItemCardVariant } from '../types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import PrintOnly from 'components/utils/PrintOnly'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import useFlightItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useFlightItemConfirmationCodes'
import type { FlightItem } from 'tripPlanner/types/tripItem'
import { calculateTravelTimeLabel } from 'tripPlanner/utils'

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(min-content, max-content) 1fr minmax(
      min-content,
      max-content
    );
  grid-gap: ${rem(16)};
  justify-content: space-between;
  align-items: center;
`

function formatDayOrDate(day: number | undefined, date: Moment | undefined) {
  if (day) {
    return `Day ${day}`
  } else if (date) {
    return date.format(DMY_CASUAL_SHORT_FORMAT)
  }
}

interface Props {
  item: FlightItem
  variant: ItemCardVariant
}

function FlightItemCardContent(props: Props) {
  const { fareTypeLabelMap, fareCabinLabelMap } = useFlightFareLabels()
  const intl = useIntl()
  const timeFormat = useRegionTimeFormat()
  const { item, variant } = props
  const {
    startDay,
    startDate,
    startTime,
    endDay,
    endDate,
    endTime,
    startUtcDateTime,
    endUtcDateTime,
  } = item

  const shouldCalculateFlightTime = startUtcDateTime && endUtcDateTime

  const duration =
    shouldCalculateFlightTime &&
    calculateTravelTimeLabel(startUtcDateTime, endUtcDateTime)

  const travellerCount: App.Occupants = item.savedItemData?.travellerCounts ?? {
    adults: 0,
  }
  const fareType = item.savedItemData ?
    fareTypeLabelMap[item.savedItemData.fareType] :
    undefined
  const fareClass = item.savedItemData ?
    fareCabinLabelMap[item.savedItemData.fareClass as keyof typeof fareCabinLabelMap] :
    undefined

  const sep = intl.formatMessage(middleDotSeparatorMessage)
  const additionalDetails = [
    fareType,
    fareClass,
    formatOccupantsShort(travellerCount),
  ]
    .filter(Boolean)
    .join(` ${sep} `)

  const confirmationCode = useFlightItemConfirmationCodes(item)

  return (
    <VerticalSpacer gap={12}>
      <FlightTopContent item={item} variant={variant} />
      <VerticalSpacer gap={4}>
        <FlightItemCardHeading item={item} />
        {confirmationCode && (
          <PrintOnly>
            <BodyText variant="medium">
              <BodyText as="span" variant="medium" weight="bold">
                {confirmationCode.label}:{' '}
              </BodyText>
              {confirmationCode.codeString}
            </BodyText>
          </PrintOnly>
        )}
        {additionalDetails && (
          <BodyText variant="medium">{additionalDetails}</BodyText>
        )}
      </VerticalSpacer>
      <Layout>
        <FlightLocationGroup
          flightDate={formatDayOrDate(startDay, startDate)}
          flightTime={startTime?.format(timeFormat)}
          airportCode={item.originAirportCode}
          item={item}
          variant={variant}
          canShowWarning
        />
        <FlightDurationArrow duration={duration} />
        <FlightLocationGroup
          flightDate={formatDayOrDate(endDay, endDate)}
          flightTime={endTime?.format(timeFormat)}
          airportCode={item.destinationAirportCode}
          variant={variant}
          item={item}
        />
      </Layout>
    </VerticalSpacer>
  )
}

export default React.memo(FlightItemCardContent)
